import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Eye, EyeOff } from 'lucide-react';
import { AuthLayout, AuthAlert } from './AuthLayout';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useLocaleStore } from '@/stores/locale';
import { OtpInput } from './OtpInput';
import { useResendCooldown } from './useResendCooldown';
import { signUp, verifySignupOtp, resendSignupCode } from './api';
import { authErrorMessage } from '@/lib/api-errors';
import { hasDirectoryClaim } from '@/lib/directory-claim';

const MARKETING_URL = (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://wrapforce.io';

function buildSchema(t: TFunction<'auth'>) {
  return z.object({
    fullName: z.string().trim().min(1, t('validation.nameRequired')).max(120),
    email: z.string().trim().toLowerCase().email(t('validation.emailInvalid')),
    password: z.string().min(10, t('validation.passwordMinLength')),
    acceptedTerms: z.boolean().refine((v) => v, { message: t('validation.termsRequired') }),
  });
}
type FormValues = { fullName: string; email: string; password: string; acceptedTerms: boolean };

export function SignupPage() {
  const { t } = useTranslation('auth');
  const locale = useLocaleStore((s) => s.locale);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');
  const prefillEmail = params.get('email');
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [email, setEmail] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => buildSchema(t), [locale]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { acceptedTerms: false, ...(prefillEmail ? { email: prefillEmail } : {}) },
  });

  async function onSubmit(values: FormValues) {
    setAuthError(null);
    try {
      const { alreadyRegistered } = await signUp(values.fullName, values.email, values.password);
      if (alreadyRegistered) {
        setError('email', { message: t('signup.emailTaken') });
        return;
      }
      setEmail(values.email);
      setStep('verify');
    } catch (err) {
      setAuthError(authErrorMessage(err, t('signup.createError')));
    }
  }

  if (step === 'verify') {
    return <VerifyStep email={email} onVerified={() => navigate(next || '/onboarding', { replace: true })} />;
  }

  return (
    <AuthLayout
      title={next ? t('signup.titleInvite') : t('signup.titleTrial')}
      subtitle={next ? t('signup.subtitleInvite') : t('signup.subtitleTrial')}
    >
      {hasDirectoryClaim() ? <p className="mb-4 text-sm text-muted">{t('directoryClaim.notice')}</p> : null}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        {authError ? <AuthAlert>{authError}</AuthAlert> : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="fullName">{t('signup.fullNameLabel')}</Label>
          <Input id="fullName" autoComplete="name" {...register('fullName')} />
          <FieldError>{errors.fullName?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">{t('signup.emailLabel')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            readOnly={!!prefillEmail}
            className={prefillEmail ? 'cursor-default text-muted' : undefined}
            {...register('email')}
          />
          <FieldError>{errors.email?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">{t('signup.passwordLabel')}</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="pr-10"
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t('signup.hidePassword') : t('signup.showPassword')}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted hover:text-ink"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <FieldError>{errors.password?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="flex cursor-pointer items-start gap-2 text-sm text-muted">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
              {...register('acceptedTerms')}
            />
            <span>
              {t('signup.agreePrefix')}
              <a href={`${MARKETING_URL}/terms`} target="_blank" rel="noreferrer" className="font-semibold text-accent-ink hover:underline">
                {t('signup.termsLink')}
              </a>
              {t('signup.agreeAnd')}
              <a href={`${MARKETING_URL}/privacy`} target="_blank" rel="noreferrer" className="font-semibold text-accent-ink hover:underline">
                {t('signup.privacyLink')}
              </a>
              {t('signup.agreeSuffix')}
            </span>
          </label>
          <FieldError>{errors.acceptedTerms?.message}</FieldError>
        </div>
        <Button type="submit" loading={isSubmitting} className="mt-2 w-full">
          {t('signup.submit')}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        {t('signup.alreadyHaveWorkspace')}{' '}
        <Link to={next ? `/login?next=${encodeURIComponent(next)}` : '/login'} className="font-semibold text-accent-ink hover:underline">
          {t('signup.signIn')}
        </Link>
      </p>
    </AuthLayout>
  );
}

function VerifyStep({ email, onVerified }: { email: string; onVerified: () => void }) {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const cooldown = useResendCooldown();

  async function handleVerify(value: string) {
    if (value.length !== 8) return;
    setError(null);
    setVerifying(true);
    try {
      await verifySignupOtp(email, value);
      onVerified();
    } catch {
      setError(t('signup.verifyError'));
      setVerifying(false);
    }
  }

  async function handleResend() {
    if (!cooldown.canResend) return;
    try {
      await resendSignupCode(email);
      cooldown.start();
    } catch {
      setError(t('otp.resendError'));
    }
  }

  return (
    <AuthLayout title={t('signup.verifyTitle')} subtitle={t('signup.verifySubtitle', { email })}>
      <div className="flex flex-col gap-4">
        {error ? <AuthAlert>{error}</AuthAlert> : null}
        <OtpInput
          value={code}
          onChange={(v) => {
            setCode(v);
            if (v.length === 8) void handleVerify(v);
          }}
        />
        <Button onClick={() => handleVerify(code)} loading={verifying} disabled={code.length !== 8}>
          {t('signup.verifyCta')}
        </Button>
        <button
          type="button"
          onClick={handleResend}
          disabled={!cooldown.canResend}
          className="text-center text-sm font-semibold text-accent-ink disabled:text-muted disabled:no-underline hover:underline"
        >
          {cooldown.canResend ? t('otp.resendCode') : t('otp.resendCodeIn', { seconds: cooldown.secondsLeft })}
        </button>
      </div>
    </AuthLayout>
  );
}
